import React, { useContext, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Paper from '@material-ui/core/Paper';
import Grow from '@material-ui/core/Grow';
import Hidden from '@material-ui/core/Hidden';
import Popper from '@material-ui/core/Popper';
import Divider from '@material-ui/core/Divider';
import AccountCircle from '@material-ui/icons/AccountCircleOutlined';
import HowToReg from '@material-ui/icons/HowToRegOutlined';
import Button from 'components/CustomButtons/Button';
import { AuthConsumer } from 'components/Auth/authContext';
import IconButton from 'components/CustomButtons/IconButton';
import styles from 'assets/jss/material-dashboard-pro-react/components/dashboardNavbarLinksStyle';
import { translate } from 'components/Translation';
import moment from 'moment';
import { BodyM, BodyMBold, BodyS, HeadingL, HeadingSBold } from 'components/Typography/Typography';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import ChangePasswordDialog from 'views/Auth/ChangePasswordDialog';
import EditIcon from '@material-ui/icons/Edit';
import ChangeNumberDialog from 'views/Auth/ChangeNumber/ChangeNumberDialog';
import ChangeEmailDialog from 'views/Auth/ChangeEmail/ChangeEmailDialog';
import useNewTheme from 'commons/hooks/newtheme/useNewTheme';
import CanPerform from 'components/CanPerform/CanPerform';

// @ts-ignore
const useStyle = makeStyles(styles);

interface IDashboardNavbarLinksProps {
	rtlActive?: boolean;
}

const DashboardNavbarLinks = ({ rtlActive }: IDashboardNavbarLinksProps) => {
	const [openProfile, setOpenProfile] = useState(null);
	const [isChangePasswordShown, setIsChangePasswordShown] = useState<boolean>(false);
	const [isChangeNumberShown, setIsChangeNumberShown] = useState<boolean>(false);
	const [isChangeEmailShown, setIsChangeEmailShown] = useState<boolean>(false);
	const classes = useStyle();
	const isNewHeaderVisible = useNewTheme();

	const handleClickProfile = (event: React.MouseEvent<any>) => {
		// @ts-ignore
		if (!!openProfile && openProfile.contains(event.target)) {
			setOpenProfile(null);
		} else {
			setOpenProfile(event.currentTarget);
		}
	};
	const handleCloseProfile = () => {
		setOpenProfile(null);
	};
	const wrapper = classNames({
		[classes.wrapperRTL]: rtlActive,
	});
	const managerClasses = classNames({
		[classes.managerClasses]: true,
	});
	const accountPictureClasses = classNames({
		[classes.headerLinksSvg]: true,
		[classes.links]: true,
		[classes.linksRTL]: rtlActive,
		[classes.accountPicture]: true,
	});
	const muiClasses = classNames({
		[classes.labelRTL]: rtlActive,
	});
	const popperClasses = classNames({
		[classes.popperClose]: !openProfile,
		[classes.popperResponsive]: true,
		[classes.popperNav]: true,
	});
	const paperClasses = classNames({
		[classes.dropdown]: true,
		[classes.paper]: true,
		[classes.userProfile]: true,
	});

	const usernameClasses = classNames({
		[classes.username]: true,
		[classes.accountName]: true,
	});

	const accountButtonClasses = classNames({
		[classes.buttonLinkRTL]: rtlActive,
		[classes.buttonLink]: !rtlActive,
		[classes.accountButton]: true,
	});

	const caretClasses = classNames({
		[classes.caret]: true,
		[classes.accountCaret]: true,
	});

	const greeting = (() => {
		const breakpoints = {
			morning: moment('05:00 AM', 'HH:mm A'),
			afternoon: moment('12:00 PM', 'HH:mm A'),
			evening: moment('05:00 PM', 'HH:mm A'),
		};
		const now = moment();

		if (now.isBetween(breakpoints.morning, breakpoints.afternoon, undefined, '[)')) {
			return 'good.morning';
		} else if (now.isBetween(breakpoints.afternoon, breakpoints.evening, undefined, '[)')) {
			return 'good.afternoon';
		} else {
			return 'good.evening';
		}
	})();

	const hideChangePassword = () => {
		setIsChangePasswordShown(false);
	};

	const showChangePassword = () => {
		setIsChangePasswordShown(true);
	};

	const hideChangeNumber = () => {
		setIsChangeNumberShown(false);
	};

	const showChangeNumber = () => {
		setIsChangeNumberShown(true);
	};

	const hideChangeEmail = () => {
		setIsChangeEmailShown(false);
	};

	const showChangeEmail = () => {
		setIsChangeEmailShown(true);
	};

	return (
		<div className={wrapper}>
			<ConditionalRender
				show={isChangePasswordShown}
				onTrue={() => (
					<AuthConsumer>
						{({ user }) => (
							<ChangePasswordDialog
								hideChangePassword={hideChangePassword}
								msisdn={user.contactNumber1}
							/>
						)}
					</AuthConsumer>
				)}
			/>
			<AuthConsumer>
				{({ logout, user, login }) => (
					<div className={managerClasses}>
						<ConditionalRender
							show={isChangeEmailShown}
							onTrue={() => (
								<ChangeEmailDialog hideChangeEmail={hideChangeEmail} msisdn={user.contactNumber1} />
							)}
						/>
						<ConditionalRender
							show={isChangeNumberShown}
							onTrue={() => (
								<ChangeNumberDialog hideChangeNumber={hideChangeNumber} msisdn={user.contactNumber1} />
							)}
						/>
						<Button
							color={isNewHeaderVisible ? 'primary-dark-v2' : 'tertiary-light'}
							onClick={handleClickProfile}
							className={accountButtonClasses}
							id="profile-menu-list"
							muiClasses={{
								label: muiClasses,
							}}
						>
							{isNewHeaderVisible ? (
								<>
									<img src="/user.svg" height={24} width={24} alt={translate('nav.user.profile')} />
								</>
							) : (
								<>
									<Hidden mdUp implementation="css">
										<AccountCircle className={accountPictureClasses} />
									</Hidden>
									<Hidden smDown implementation="css">
										<div className={classes.accountButtonLabel}>
											<div className={classes.greeting}>
												<HeadingL color="secondary-light" disableMargin>
													{translate(greeting)}
												</HeadingL>
											</div>
											<AccountCircle className={accountPictureClasses} />
											<div className={usernameClasses}>
												<BodyMBold
													color="brand"
													disableMargin
												>{`${login}, ${user?.accountName}`}</BodyMBold>
											</div>
										</div>
									</Hidden>
									<b className={caretClasses} />
								</>
							)}
						</Button>
						<Popper
							open={Boolean(openProfile)}
							anchorEl={openProfile}
							transition
							disablePortal
							placement="bottom-end"
							className={popperClasses}
						>
							{({ TransitionProps }) => (
								<Grow {...TransitionProps} style={{ transformOrigin: '0 0 0' }}>
									<Paper className={paperClasses}>
										<ClickAwayListener onClickAway={handleCloseProfile}>
											<MenuList role="menu" className={classes.menuList}>
												<MenuItem className={classes.menuItem}>
													<div className={classes.welcome}>
														<div>
															<BodyS>{translate('login.welcome')}</BodyS>
															<HeadingSBold>{translate('login.enterprise')}</HeadingSBold>
														</div>
														<div className={classes.logoWrapper}>
															<img src="/logo-color.svg" alt={translate('nav.logo')} />
														</div>
													</div>
													<div className={classes.userInfo}>
														<IconButton className={classes.profileIconButton}>
															<HowToReg />
														</IconButton>
														<div className={classes.userInfoWrapper}>
															<div className={classes.dropdownUsername}>
																<BodyM color="brand">
																	{`${login}, ${user.accountName}`}
																</BodyM>
															</div>
															<div className={classes.userType}>
																<BodyS color="secondary-light">{user.userType}</BodyS>
															</div>
															<div className={classes.userNumber}>
																<BodyS color="secondary-light">{user.email}</BodyS>
																<CanPerform
																	action="profile:change_email"
																	yes={
																		<Button
																			color="tertiary-light"
																			onClick={showChangeEmail}
																			className={classes.userNumberEditButton}
																		>
																			<EditIcon
																				className={classes.userNumberEditIcon}
																			/>
																		</Button>
																	}
																/>
															</div>
															<CanPerform
																action="profile:change_number"
																yes={
																	<div className={classes.userNumber}>
																		<BodyS color="secondary-light">
																			{user.contactNumber1}
																		</BodyS>
																		<Button
																			color="tertiary-light"
																			onClick={showChangeNumber}
																			className={classes.userNumberEditButton}
																		>
																			<EditIcon
																				className={classes.userNumberEditIcon}
																			/>
																		</Button>
																	</div>
																}
															/>
														</div>
													</div>
												</MenuItem>
												<Divider light />
												<MenuItem className={classes.dropdownButtonContainer}>
													<Button
														onClick={logout}
														color="tertiary-light"
														id="logout-button"
														className={classes.dropdownButton}
													>
														{translate('navbar.log.out')}
													</Button>
													<Button
														onClick={showChangePassword}
														color="tertiary-light"
														id="change-password-button"
														className={classes.dropdownButton}
													>
														{translate('navbar.change.password')}
													</Button>
												</MenuItem>
											</MenuList>
										</ClickAwayListener>
									</Paper>
								</Grow>
							)}
						</Popper>
					</div>
				)}
			</AuthConsumer>
		</div>
	);
};
export default DashboardNavbarLinks;
